import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Modal, Form, Input, message,
  Popconfirm, Tag, Row, Col, Tooltip
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined
} from '@ant-design/icons';
import axios from 'axios';
import AuthService from '../utils/auth';
import dayjs from 'dayjs';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3060/api';

// 防抖
function useDebouncedCallback(fn, delay) {
  const timer = React.useRef(null);
  return useCallback((...args) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => fn(...args), delay);
  }, [fn, delay]);
}

const FlightBatchManagement = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRoute, setEditingRoute] = useState(null);
  const [keyword, setKeyword] = useState('');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async (kw) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kw) params.append('keyword', kw);
      const response = await axios.get(`${API_BASE}/flight-routes?${params.toString()}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        setRoutes(response.data.data);
      }
    } catch (error) {
      message.error('获取航班信息列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setKeyword(value);
    fetchRoutes(value);
  };

  // 航班号自动匹配
  const lookupFlightNo = useCallback(async (flightNo) => {
    if (!flightNo || flightNo.length < 3) return;
    try {
      const res = await axios.get(
        `${API_BASE}/flight-routes/lookup/${encodeURIComponent(flightNo.toUpperCase())}`,
        { headers: AuthService.getAuthHeaders() }
      );
      if (res.data.success && res.data.data) {
        const route = res.data.data;
        form.setFieldsValue({
          airline: route.airline,
          fromCity: route.fromCity,
          toCity: route.toCity,
          departureTime: route.departureTime,
          arrivalTime: route.arrivalTime
        });
        message.success(`已自动填充航班 ${flightNo.toUpperCase()} 的信息`);
      }
    } catch {
      // 未找到，静默
    }
  }, [form]);

  const debouncedLookup = useDebouncedCallback(lookupFlightNo, 500);

  const handleAdd = () => {
    setEditingRoute(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRoute(record);
    form.setFieldsValue({
      flightNo: record.flightNo,
      airline: record.airline,
      fromCity: record.fromCity,
      toCity: record.toCity,
      departureTime: record.departureTime,
      arrivalTime: record.arrivalTime
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`${API_BASE}/flight-routes/${id}`, {
        headers: AuthService.getAuthHeaders()
      });
      if (response.data.success) {
        message.success('删除成功');
        fetchRoutes(keyword);
      }
    } catch (error) {
      message.error(error.response?.data?.message || '删除失败');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingRoute) {
        await axios.put(`${API_BASE}/flight-routes/${editingRoute.id}`, values, {
          headers: AuthService.getAuthHeaders()
        });
        message.success('更新成功');
      } else {
        await axios.post(`${API_BASE}/flight-routes`, values, {
          headers: AuthService.getAuthHeaders()
        });
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchRoutes(keyword);
    } catch (error) {
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      }
    }
  };

  const columns = [
    {
      title: '航班号',
      dataIndex: 'flightNo',
      key: 'flightNo',
      width: 100,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: '航司',
      dataIndex: 'airline',
      key: 'airline',
      width: 120
    },
    {
      title: '出发城市',
      dataIndex: 'fromCity',
      key: 'fromCity',
      width: 120
    },
    {
      title: '到达城市',
      dataIndex: 'toCity',
      key: 'toCity',
      width: 120
    },
    {
      title: '起飞时间',
      dataIndex: 'departureTime',
      key: 'departureTime',
      width: 90
    },
    {
      title: '到达时间',
      dataIndex: 'arrivalTime',
      key: 'arrivalTime',
      width: 90
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: (date) => dayjs(date).format('YYYY-MM-DD HH:mm')
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="编辑">
            <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="确定删除该航班信息吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="航班信息管理"
        extra={
          <Space>
            <Input.Search
              placeholder="搜索航班号/航司/城市"
              allowClear
              onSearch={handleSearch}
              style={{ width: 220 }}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新建航班
            </Button>
          </Space>
        }
      >
        <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>
          此处管理所有已保存的航班信息，通知书工作台中录入的航班会自动保存到这里
        </div>
        <Table
          columns={columns}
          dataSource={routes}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 15 }}
          scroll={{ x: 900 }}
          size="small"
        />
      </Card>

      <Modal
        title={editingRoute ? '编辑航班信息' : '新建航班信息'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => setModalVisible(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="flightNo"
            label="航班号"
            rules={[{ required: true, message: '请输入航班号' }]}
          >
            <Input
              placeholder="如：MU780"
              onChange={(e) => {
                if (!editingRoute) {
                  debouncedLookup(e.target.value);
                }
              }}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="airline" label="航司">
                <Input placeholder="如：中国东方航空" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fromCity" label="出发城市">
                <Input placeholder="如：上海" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="toCity" label="到达城市">
                <Input placeholder="如：奥克兰" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="departureTime" label="起飞时间">
                <Input placeholder="如：12:00" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="arrivalTime" label="到达时间">
                <Input placeholder="如：18:30" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default FlightBatchManagement;
